/**
 * Filter Store with Scoped State
 * 
 * Each doctype must use its own scope key to prevent filter collisions:
 * - Item: "inventory.items"
 * - Sales Invoice: "selling.sales_invoice"
 * - Future doctypes: choose their own unique scope key
 * 
 * Legacy code using state.filters / state.setFilters will continue to work
 * (maps to "inventory.items" scope for backwards compatibility).
 */

import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { FilterDescriptor } from "@/lib/erp/filterTypes";

interface FilterState {
  // Scoped filters: map of scope -> FilterDescriptor[]
  scopedFilters: Record<string, FilterDescriptor[]>;
  
  // Scoped APIs
  getFilters: (scope: string) => FilterDescriptor[];
  setFiltersForKey: (scope: string, filters: FilterDescriptor[]) => void;
  addFilterForKey: (scope: string, filter: FilterDescriptor) => void;
  removeFilterForKey: (scope: string, index: number) => void;
  updateFilterForKey: (scope: string, index: number, updates: Partial<FilterDescriptor>) => void;
  clearFiltersForKey: (scope: string) => void;
  
  // Legacy APIs (backwards compatible - maps to "inventory.items")
  filters: FilterDescriptor[];
  setFilters: (filters: FilterDescriptor[]) => void;
  addFilter: (filter: FilterDescriptor) => void;
  removeFilter: (index: number) => void;
  updateFilter: (index: number, updates: Partial<FilterDescriptor>) => void;
  clearFilters: () => void;
}

const DEFAULT_SCOPE = "inventory.items";

function resolveZustandStorage(): { getItem: (name: string) => string | null; setItem: (name: string, value: string) => void; removeItem: (name: string) => void } {
  // In SSR / some test environments, localStorage may be missing or partially implemented.
  // Persist middleware requires a storage with setItem/getItem/removeItem; fall back to no-op storage.
  const fallback = {
    getItem: (_name: string) => null,
    setItem: (_name: string, _value: string) => {},
    removeItem: (_name: string) => {},
  };

  if (typeof window === "undefined") return fallback;

  const ls: any = (window as any).localStorage;
  if (ls && typeof ls.getItem === "function" && typeof ls.setItem === "function" && typeof ls.removeItem === "function") {
    return ls;
  }

  return fallback;
}

export const useFilterStore = create<FilterState>()(
  persist(
    (set, get) => ({
      scopedFilters: {
        [DEFAULT_SCOPE]: [],
      },

      // Scoped APIs
      getFilters: (scope: string) => {
        return get().scopedFilters[scope] || [];
      },

      setFiltersForKey: (scope: string, filters: FilterDescriptor[]) => {
        set((state) => ({
          scopedFilters: {
            ...state.scopedFilters,
            [scope]: filters,
          },
        }));
      },

      addFilterForKey: (scope: string, filter: FilterDescriptor) => {
        set((state) => {
          const currentFilters = state.scopedFilters[scope] || [];
          return {
            scopedFilters: {
              ...state.scopedFilters,
              [scope]: [...currentFilters, filter],
            },
          };
        });
      },

      removeFilterForKey: (scope: string, index: number) => {
        set((state) => {
          const currentFilters = state.scopedFilters[scope] || [];
          return {
            scopedFilters: {
              ...state.scopedFilters,
              [scope]: currentFilters.filter((_, i) => i !== index),
            },
          };
        });
      },

      updateFilterForKey: (scope: string, index: number, updates: Partial<FilterDescriptor>) => {
        set((state) => {
          const currentFilters = state.scopedFilters[scope] || [];
          const newFilters = [...currentFilters];
          if (newFilters[index]) {
            newFilters[index] = { ...newFilters[index], ...updates };
          }
          return {
            scopedFilters: {
              ...state.scopedFilters,
              [scope]: newFilters,
            },
          };
        });
      },

      clearFiltersForKey: (scope: string) => {
        set((state) => ({
          scopedFilters: {
            ...state.scopedFilters,
            [scope]: [],
          },
        }));
      },

      // Legacy APIs (backwards compatible)
      get filters() {
        return get().getFilters(DEFAULT_SCOPE);
      },

      setFilters: (filters: FilterDescriptor[]) => {
        get().setFiltersForKey(DEFAULT_SCOPE, filters);
      },

      addFilter: (filter: FilterDescriptor) => {
        get().addFilterForKey(DEFAULT_SCOPE, filter);
      },

      removeFilter: (index: number) => {
        get().removeFilterForKey(DEFAULT_SCOPE, index);
      },

      updateFilter: (index: number, updates: Partial<FilterDescriptor>) => {
        get().updateFilterForKey(DEFAULT_SCOPE, index, updates);
      },

      clearFilters: () => {
        get().clearFiltersForKey(DEFAULT_SCOPE);
      },
    }),
    {
      name: "filter-storage",
      // Only persist scopedFilters, not functions
      partialize: (state) => ({
        scopedFilters: state.scopedFilters,
      }),
      // Explicit, safe storage selection (never throws in SSR/tests).
      storage: createJSONStorage(() => resolveZustandStorage() as any),
    }
  )
);

